import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getStatisticsPlainAccordionSx,
  getStatisticsSectionHeaderRowSx,
  getStatisticsTotalsRowSx,
} from '../../util/chartTheme';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';

const euroFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const decimalFmt = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const intFmt = new Intl.NumberFormat('de-DE');

const formatValue = (raw, unit) => {
  if (raw == null || Number.isNaN(raw)) return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '—';
  if (unit === '€') return euroFmt.format(n);
  if (unit === '%') return `${decimalFmt.format(n)} %`;
  if (unit === 'T') return `${decimalFmt.format(n)} T`;
  if (unit === 'sec') return `${decimalFmt.format(n)} s`;
  return intFmt.format(n);
};

// Build pivot rows: one per branch, each with:
// - entertainments: [{ entertainmentId, name, byDay: {date:val}, total }]
// - byDay: { date: val }   (per-day sum / avg across entertainments)
// - total: scalar
const buildPivot = (matrix, metricKey, isAverage) => {
  const days = Array.isArray(matrix?.days) ? matrix.days : [];
  const ents = Array.isArray(matrix?.entertainments) ? matrix.entertainments : [];

  const branchMap = new Map();
  for (const e of ents) {
    const branchKey = e.branchId || '__none__';
    if (!branchMap.has(branchKey)) {
      branchMap.set(branchKey, {
        branchId: e.branchId || null,
        branchName: e.branchName || '—',
        entertainments: [],
      });
    }
    const byDay = e.byDay || {};
    let entSum = 0;
    let entCount = 0;
    const entByDay = {};
    for (const d of days) {
      const row = byDay[d];
      if (row && row[metricKey] != null) {
        const v = Number(row[metricKey]) || 0;
        entByDay[d] = v;
        entSum += v;
        entCount += 1;
      } else {
        entByDay[d] = null;
      }
    }
    const entTotal = isAverage ? (entCount ? entSum / entCount : 0) : entSum;
    branchMap.get(branchKey).entertainments.push({
      entertainmentId: e.entertainmentId,
      name: e.entertainmentName || '—',
      byDay: entByDay,
      total: entTotal,
    });
  }

  const branches = [...branchMap.values()].map(branch => {
    const byDay = {};
    for (const d of days) {
      let sum = 0;
      let count = 0;
      for (const e of branch.entertainments) {
        const v = e.byDay[d];
        if (v != null) {
          sum += v;
          count += 1;
        }
      }
      byDay[d] = isAverage ? (count ? sum / count : 0) : sum;
    }
    let total = 0;
    let countEnts = branch.entertainments.length;
    for (const e of branch.entertainments) total += e.total || 0;
    if (isAverage && countEnts) total = total / countEnts;
    return { ...branch, byDay, total };
  });

  branches.sort((a, b) =>
    String(a.branchName).localeCompare(String(b.branchName), 'de')
  );

  const grandByDay = {};
  let grandTotal = 0;
  for (const d of days) {
    let v = 0;
    for (const b of branches) v += Number(b.byDay[d]) || 0;
    grandByDay[d] = v;
  }
  for (const b of branches) grandTotal += Number(b.total) || 0;

  return { days, branches, grandByDay, grandTotal };
};

const BranchRow = React.memo(function BranchRow({
  branch,
  days,
  expanded,
  onToggle,
  unit,
}) {
  const theme = useTheme();
  return (
    <>
      <TableRow sx={getStatisticsSectionHeaderRowSx(theme)}>
        <TableCell sx={{ width: 52, borderBottom: 'none' }}>
          <IconButton size='small' onClick={onToggle} aria-label='expand'>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 700 }}>{branch.branchName}</TableCell>
        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatValue(branch.total, unit)}
        </TableCell>
        {days.map(d => (
          <TableCell key={d} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {formatValue(branch.byDay[d], unit)}
          </TableCell>
        ))}
      </TableRow>
      {expanded &&
        branch.entertainments.map(e => (
          <TableRow key={e.entertainmentId || e.name}>
            <TableCell />
            <TableCell sx={{ pl: 3 }}>{e.name}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {formatValue(e.total, unit)}
            </TableCell>
            {days.map(d => (
              <TableCell key={d} sx={{ whiteSpace: 'nowrap' }}>
                {formatValue(e.byDay[d], unit)}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  );
});

const MetricPivotTable = React.memo(function MetricPivotTable({
  matrix,
  metric,
  defaultExpandedAllBranches = false,
}) {
  const theme = useTheme();
  const { days, branches, grandByDay, grandTotal } = useMemo(
    () => buildPivot(matrix, metric.key, !!metric.isAverage),
    [matrix, metric]
  );
  const [expanded, setExpanded] = useState(() => {
    if (!defaultExpandedAllBranches) return {};
    const init = {};
    for (const b of branches) init[b.branchId || '__none__'] = true;
    return init;
  });
  const toggle = id =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const unit = metric.unit;
  const showGesamt = !metric.isAverage;

  if (!days.length || !branches.length) {
    return null;
  }

  return (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 52 }} />
            <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>
              Standort / Entertainment
            </TableCell>
            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              Summe
            </TableCell>
            {days.map(d => (
              <TableCell key={d} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {dayjs(d).format('ddd, DD.MM.')}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {branches.map(b => (
            <BranchRow
              key={b.branchId || b.branchName}
              branch={b}
              days={days}
              expanded={!!expanded[b.branchId || '__none__']}
              onToggle={() => toggle(b.branchId || '__none__')}
              unit={unit}
            />
          ))}
        </TableBody>
        {showGesamt && (
          <TableBody>
            <TableRow sx={getStatisticsTotalsRowSx(theme)}>
              <TableCell />
              <TableCell sx={{ fontWeight: 800 }}>Gesamt</TableCell>
              <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                {formatValue(grandTotal, unit)}
              </TableCell>
              {days.map(d => (
                <TableCell key={d} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {formatValue(grandByDay[d], unit)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
});

// Renders an Accordion per metric, each containing a full pivot table.
// `matrix` has shape { days: string[], entertainments: [{ entertainmentId, entertainmentName, branchId, branchName, byDay: {[date]: {metric:val}} }] }
const StatisticMatrixTables = React.memo(function StatisticMatrixTables({
  title,
  matrix,
  metrics,
}) {
  const theme = useTheme();
  const hasData = !!(
    matrix &&
    Array.isArray(matrix.days) &&
    matrix.days.length &&
    Array.isArray(matrix.entertainments) &&
    matrix.entertainments.length
  );
  if (!hasData) return null;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {title}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {metrics.map(m => (
          <Accordion
            key={m.key}
            disableGutters
            square
            sx={{ boxShadow: 'none', '&::before': { display: 'none' } }}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                ...getStatisticsPlainAccordionSx(theme),
              }}
            >
              {m.label}
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
              <MetricPivotTable matrix={matrix} metric={m} />
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionDetails>
    </Accordion>
  );
});

export default StatisticMatrixTables;
